import React from "react"
import * as Accordion from "@radix-ui/react-accordion"
import css from "@styled-system/css"
import { useTheme } from "@emotion/react"
import { rgba } from "polished"

import { Box, Heading } from "components"

// Coerce an arbitrary value into a valid HTML5 id (no whitespace), or undefined.
// Callers pass rich-text objects or strings with spaces, which would otherwise
// produce invalid ids.
const toHtmlId = value =>
  typeof value === "string" && value.trim()
    ? value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-+|-+$)/g, "")
    : undefined

const itemValue = index => "accordionGroup" + index

// Radix renders a real <h3> and a real <button>, both of which carry user-agent styling
// that has to be reset to keep the heading rendering consistent with the rest of the site.
const HEADER_RESET = {
  margin: 0,
  fontSize: "inherit",
  fontWeight: "inherit",
  lineHeight: "inherit",
}

const TRIGGER_RESET = {
  appearance: "none",
  background: "none",
  border: 0,
  borderRadius: 0,
  m: 0,
  p: 0,
  width: "100%",
  textAlign: "left",
  font: "inherit",
  color: "inherit",
}

function AccordionGroup({
  // Defaults are parameter defaults rather than `AccordionGroup.defaultProps`:
  // React 19 no longer applies defaultProps to function components.
  items = [],
  onChange,
  preExpanded,
  ...rest
}) {
  const theme = useTheme()

  return (
    <Accordion.Root
      type="single"
      // `type="single"` + `collapsible`: one panel open at a time, and all may be closed.
      collapsible
      defaultValue={preExpanded && preExpanded[0]}
      onValueChange={value => {
        if (!onChange) return
        const item = items.find((_, index) => itemValue(index) === value)
        onChange(item && item.object)
      }}
      {...rest}
    >
      {items.map((item, index) => (
        <Accordion.Item
          key={itemValue(index)}
          value={itemValue(index)}
          id={toHtmlId(item.id)}
          css={css({
            py: ["layout.2", null, null, "layout.4"],
            borderBottom: "1px solid",
            borderColor: rgba(theme.colors.text, 0.2),
            "&:last-of-type": {
              borderBottom: "none",
            },
          })}
        >
          <Accordion.Header css={css(HEADER_RESET)}>
            <Accordion.Trigger
              css={css({
                ...TRIGGER_RESET,
                display: "flex",
                cursor: "pointer",
                "&:focus": { outline: 0 },
                ".octagonControl": {
                  opacity: 0.25,
                  transition: "opacity 400ms ease-out",
                  ".octagonControl__container": {
                    fill: "none",
                    stroke: "text",
                    transition: "fill 400ms ease-out",
                  },
                  ".octagonControl__arrow": {
                    stroke: "text",
                    transformOrigin: "50% 50%",
                    transition:
                      "stroke 200ms ease-out, transform 200ms ease-out",
                  },
                },
                // Effects when accordion is opened. Radix sets `aria-expanded` on the
                // trigger, so this selector carries over from the old implementation
                // unchanged.
                '&[aria-expanded="true"]': {
                  ".octagonControl": {
                    opacity: 1,
                    ".octagonControl__container": {
                      fill: "text",
                    },
                    ".octagonControl__arrow": {
                      transform: "rotate(180deg)",
                      stroke: "background",
                    },
                  },
                },
              })}
            >
              <Box flex={1} mr="layout.1">
                <Heading
                  children={item.heading}
                  as="span"
                  size={600}
                  display="block"
                />
                {item.subheading && (
                  <Heading
                    children={item.subheading}
                    as="span"
                    size={600}
                    display="block"
                    mt="layout.1"
                    color="alt"
                  />
                )}
              </Box>
              <Box className="octagonControl">
                <Box
                  as="svg"
                  display="block"
                  width={[32, 40, 40]}
                  height={[32, 40, 40]}
                  viewBox="0 0 48 48"
                  strokeWidth={2}
                  fill="none"
                >
                  <path
                    d="M19.5139 1.44668L24 1.00484L28.4861 1.44668L32.7999 2.75524L36.7754 4.88023L40.26 7.73996L43.1198 11.2246L45.2448 15.2001L46.5533 19.5139L46.9952 24L46.5533 28.4861L45.2448 32.7999L43.1198 36.7754L40.26 40.26L36.7754 43.1198L32.7999 45.2448L28.4861 46.5533L24 46.9952L19.5139 46.5533L15.2001 45.2448L11.2246 43.1198L7.73996 40.26L4.88023 36.7754L2.75524 32.7999L1.44668 28.4861L1.00484 24L1.44668 19.5139L2.75524 15.2001L4.88022 11.2246L7.73997 7.73997L11.2246 4.88022L15.2001 2.75524L19.5139 1.44668Z"
                    className="octagonControl__container"
                  />
                  <path
                    d="M34 27L24 17L14 27"
                    className="octagonControl__arrow"
                  />
                </Box>
              </Box>
            </Accordion.Trigger>
          </Accordion.Header>
          <Accordion.Content
            css={css({ pt: ["layout.2", null, null, "layout.4"] })}
          >
            {item.children}
          </Accordion.Content>
        </Accordion.Item>
      ))}
    </Accordion.Root>
  )
}

export default AccordionGroup
